import { useMemo } from 'react';
import { summarizeActualRows } from 'utils/budgetActuals';

/**
 * Custom hook that computes detail sheet totals locally from detailsRows.
 * No API call — totals update instantly as rows change.
 */
export const useTotalsCalculation = ({ detailsRows, selectedItem }) => {
  const rows = detailsRows[selectedItem?.id];

  const {
    preTotal,
    AppliedFringe,
    detailsTotal,
    actualTotal,
    hasActualTotal,
    hasAppliedFringe,
    usesExchangeRates,
    updatedRows,
  } = useMemo(() => {
    const currentRows = rows || [];
    let itemSum = 0;
    let fringeSum = 0;
    let hasAppliedFringeRows = false;
    let primaryCurrencyTotalSum = 0;
    let hasPrimaryCurrencyConversions = false;
    const actualSummary = summarizeActualRows(currentRows);

    const mapped = currentRows.map(row => {
      const itemTotal = parseFloat(row?.item_total) || 0;
      const fringeTotal = parseFloat(row?.fringe_total) || 0;
      const rowTotal = itemTotal + fringeTotal;
      const convertedTotal = Number.parseFloat(
        row?.column_total_primary_currency
      );
      const hasConvertedTotal = Number.isFinite(convertedTotal);
      itemSum += itemTotal;
      fringeSum += fringeTotal;
      hasAppliedFringeRows =
        hasAppliedFringeRows ||
        fringeTotal !== 0 ||
        (Array.isArray(row?.fringe)
          ? row.fringe.length > 0
          : Boolean(row?.fringe));
      primaryCurrencyTotalSum += hasConvertedTotal ? convertedTotal : rowTotal;
      hasPrimaryCurrencyConversions =
        hasPrimaryCurrencyConversions || hasConvertedTotal;
      return { ...row, fringe_total: fringeTotal };
    });

    return {
      preTotal: parseFloat(itemSum.toFixed(2)),
      AppliedFringe: parseFloat(fringeSum.toFixed(2)),
      detailsTotal: parseFloat(primaryCurrencyTotalSum.toFixed(2)),
      actualTotal: actualSummary.actualTotal,
      hasActualTotal: actualSummary.hasActuals,
      hasAppliedFringe: hasAppliedFringeRows,
      usesExchangeRates: hasPrimaryCurrencyConversions,
      updatedRows: mapped,
    };
  }, [rows]);

  return {
    detailsTotal,
    AppliedFringe,
    actualTotal,
    preTotal,
    hasActualTotal,
    hasAppliedFringe,
    usesExchangeRates,
    updatedRows,
  };
};
